import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCarouselQueue, deleteCarouselItem, getCarouselPipelineStats, approveCarouselItem, bulkApproveCarouselItems, getChessWebSocialQueue, selectChessWebVariation, deleteChessWebSocialItem, getAutoRejectedCarouselItems, restoreCarouselItem, publishChessWebSocial, getWidgetTweetQueue, selectWidgetTweetVariation, deleteWidgetTweetItem, publishWidgetTweet, publishWidgetTweetFromCarousel } from '../../services/contentOpsService';
import { PreviewModal } from './CarouselPreview';

const CAROUSEL_THRESHOLD = 5;
const MAX_TWEET_ITEMS = 8;
const MAX_QUOTE_ITEMS = 7;

const InstagramDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tweets, setTweets] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [widgetTweets, setWidgetTweets] = useState([]);
  const [rejectedItems, setRejectedItems] = useState([]);
  const [chesswebQueue, setChesswebQueue] = useState([]);
  const [widgetTweetQueue, setWidgetTweetQueue] = useState([]);
  const initialTab = searchParams.get('tab') || 'tweets';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [pipelineStats, setPipelineStats] = useState({ lastFetch: null, lastCarousel: null });
  const [removing, setRemoving] = useState(null);
  const [approving, setApproving] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const [selecting, setSelecting] = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [viewMode, setViewMode] = useState('drafts'); // 'drafts' | 'approved'

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'tweets' ? {} : { tab });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const results = await Promise.allSettled([
        getCarouselQueue(),
        getCarouselPipelineStats(),
        getChessWebSocialQueue(),
        getAutoRejectedCarouselItems(),
        getWidgetTweetQueue()
      ]);
      if (results[0].status === 'fulfilled') {
        setTweets(results[0].value.tweets || []);
        setQuotes(results[0].value.quotes || []);
        setWidgetTweets(results[0].value.widget_tweets || []);
      }
      if (results[1].status === 'fulfilled') setPipelineStats(results[1].value);
      if (results[2].status === 'fulfilled') setChesswebQueue(results[2].value);
      if (results[3].status === 'fulfilled') setRejectedItems(results[3].value || []);
      if (results[4].status === 'fulfilled') setWidgetTweetQueue(results[4].value || []);
      results.forEach((r, i) => {
        if (r.status === 'rejected') {
          const names = ['carouselQueue', 'pipelineStats', 'chesswebQueue', 'rejectedItems', 'widgetTweetQueue'];
          console.warn(`InstagramDashboard: ${names[i]} failed:`, r.reason?.message || r.reason);
        }
      });
    } catch (err) {
      console.error('InstagramDashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleRemove = async (docId) => {
    if (removing) return;
    setRemoving(docId);
    try {
      await deleteCarouselItem(docId);
      setTweets(prev => prev.filter(t => t.id !== docId));
      setQuotes(prev => prev.filter(q => q.id !== docId));
      setWidgetTweets(prev => prev.filter(w => w.id !== docId));
      setRejectedItems(prev => prev.filter(r => r.id !== docId));
    } catch (err) {
      console.error('Failed to remove item:', err);
    } finally {
      setRemoving(null);
    }
  };

  const handleRestore = async (docId) => {
    if (restoring) return;
    setRestoring(docId);
    try {
      await restoreCarouselItem(docId);
      // Move from rejected to tweets
      const item = rejectedItems.find(r => r.id === docId);
      if (item) {
        setRejectedItems(prev => prev.filter(r => r.id !== docId));
        setTweets(prev => [...prev, { ...item, status: 'pending', rejectionReason: undefined }]);
      }
    } catch (err) {
      console.error('Failed to restore item:', err);
    } finally {
      setRestoring(null);
    }
  };

  const handleApprove = async (docId) => {
    if (approving) return;
    setApproving(docId);
    // Optimistic update
    const now = new Date().toISOString();
    setTweets(prev => prev.map(t => t.id === docId ? { ...t, reviewedAt: now } : t));
    setQuotes(prev => prev.map(q => q.id === docId ? { ...q, reviewedAt: now } : q));
    try {
      await approveCarouselItem(docId);
    } catch (err) {
      console.error('Failed to approve item:', err);
      // Revert on error
      setTweets(prev => prev.map(t => t.id === docId ? { ...t, reviewedAt: undefined } : t));
      setQuotes(prev => prev.map(q => q.id === docId ? { ...q, reviewedAt: undefined } : q));
    } finally {
      setApproving(null);
    }
  };

  // ChessWeb editing state
  const [chesswebEditing, setChesswebEditing] = useState(null); // { docId, text }
  const [chesswebPublishing, setChesswebPublishing] = useState(null);

  const handleSelectVariation = async (docId, variationIndex) => {
    if (selecting) return;
    setSelecting(`${docId}-${variationIndex}`);
    // Optimistic update — set selectedVariation but keep pending status
    const item = chesswebQueue.find(i => i.id === docId);
    const text = item?.variations?.[variationIndex]?.text || '';
    setChesswebQueue(prev => prev.map(i =>
      i.id === docId ? { ...i, selectedVariation: variationIndex, editedText: text } : i
    ));
    // Populate editor
    setChesswebEditing({ docId, text });
    try {
      await selectChessWebVariation(docId, variationIndex);
    } catch (err) {
      console.error('Failed to select variation:', err);
      loadData();
    } finally {
      setSelecting(null);
    }
  };

  const handlePublishChessweb = async (docId) => {
    if (chesswebPublishing) return;
    const text = chesswebEditing?.text;
    if (!text?.trim()) return;
    setChesswebPublishing(docId);
    try {
      await publishChessWebSocial(docId, text);
      // Remove from queue — it's now in Post-Bridge
      setChesswebQueue(prev => prev.filter(i => i.id !== docId));
      setChesswebEditing(null);
    } catch (err) {
      console.error('Failed to publish chessweb social:', err);
      alert('Publish failed: ' + (err.message || 'Unknown error'));
    } finally {
      setChesswebPublishing(null);
    }
  };

  const handleRemoveChessweb = async (docId) => {
    if (removing) return;
    setRemoving(docId);
    try {
      await deleteChessWebSocialItem(docId);
      setChesswebQueue(prev => prev.filter(item => item.id !== docId));
    } catch (err) {
      console.error('Failed to remove chessweb item:', err);
    } finally {
      setRemoving(null);
    }
  };

  // Widget tweet queue editing state
  const [widgetTweetEditing, setWidgetTweetEditing] = useState(null); // { docId, text }
  const [widgetTweetPublishing, setWidgetTweetPublishing] = useState(null);

  const handleSelectWidgetVariation = async (docId, variationIndex) => {
    if (selecting) return;
    setSelecting(`wt-${docId}-${variationIndex}`);
    const item = widgetTweetQueue.find(i => i.id === docId);
    const text = item?.variations?.[variationIndex]?.text || '';
    setWidgetTweetQueue(prev => prev.map(i =>
      i.id === docId ? { ...i, selectedVariation: variationIndex, editedText: text } : i
    ));
    setWidgetTweetEditing({ docId, text });
    try {
      await selectWidgetTweetVariation(docId, variationIndex);
    } catch (err) {
      console.error('Failed to select widget tweet variation:', err);
      loadData();
    } finally {
      setSelecting(null);
    }
  };

  const handlePublishWidgetTweet = async (docId) => {
    if (widgetTweetPublishing) return;
    const text = widgetTweetEditing?.text;
    if (!text?.trim()) return;
    setWidgetTweetPublishing(docId);
    try {
      await publishWidgetTweet(docId, text);
      setWidgetTweetQueue(prev => prev.filter(i => i.id !== docId));
      setWidgetTweetEditing(null);
    } catch (err) {
      console.error('Failed to publish widget tweet:', err);
      alert('Publish failed: ' + (err.message || 'Unknown error'));
    } finally {
      setWidgetTweetPublishing(null);
    }
  };

  const handleRemoveWidgetTweet = async (docId) => {
    if (removing) return;
    setRemoving(docId);
    try {
      await deleteWidgetTweetItem(docId);
      setWidgetTweetQueue(prev => prev.filter(item => item.id !== docId));
    } catch (err) {
      console.error('Failed to remove widget tweet item:', err);
    } finally {
      setRemoving(null);
    }
  };

  const [carouselPublishing, setCarouselPublishing] = useState(null);

  const handlePublishCarouselWidget = async (carouselItem) => {
    if (carouselPublishing) return;
    setCarouselPublishing(carouselItem.id);
    try {
      await publishWidgetTweetFromCarousel(carouselItem.id, carouselItem);
      // Remove from widget tweets display
      setWidgetTweets(prev => prev.filter(i => i.id !== carouselItem.id));
    } catch (err) {
      console.error('Failed to publish widget carousel item:', err);
      alert('Publish failed: ' + (err.message || 'Unknown error'));
    } finally {
      setCarouselPublishing(null);
    }
  };

  const handleBulkApprove = async () => {
    const items = activeTab === 'tweets' ? tweets : activeTab === 'quotes' ? quotes : activeTab === 'widget_tweets' ? widgetTweets : [];
    const unreviewedIds = items.filter(i => !i.reviewedAt).map(i => i.id);
    if (unreviewedIds.length === 0) return;
    setBulkApproving(true);
    const now = new Date().toISOString();
    const updater = prev => prev.map(i => unreviewedIds.includes(i.id) ? { ...i, reviewedAt: now } : i);
    if (activeTab === 'tweets') setTweets(updater);
    else if (activeTab === 'quotes') setQuotes(updater);
    else if (activeTab === 'widget_tweets') setWidgetTweets(updater);
    try {
      await bulkApproveCarouselItems(unreviewedIds);
    } catch (err) {
      console.error('Failed to bulk approve:', err);
      loadData();
    } finally {
      setBulkApproving(false);
    }
  };

  const handlePreview = (item, type) => {
    setPreviewItem(item);
    setPreviewType(type);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Never';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const tweetCount = tweets.length;
  const quoteCount = quotes.length;
  const widgetTweetCount = widgetTweets.length;
  const widgetQueuePending = widgetTweetQueue.filter(i => i.status !== 'posted').length;
  const rejectedCount = rejectedItems.length;
  const chesswebCount = chesswebQueue.filter(i => i.status !== 'posted').length;

  // Filtered counts for tab labels based on viewMode
  const filterByView = (items) => viewMode === 'drafts'
    ? items.filter(i => !i.reviewedAt)
    : items.filter(i => !!i.reviewedAt);
  const filteredTweetCount = filterByView(tweets).length;
  const filteredQuoteCount = filterByView(quotes).length;
  const filteredWidgetTweetCount = filterByView(widgetTweets).length;
  const chesswebPending = chesswebQueue.filter(i => i.status === 'pending' || !i.status).length;
  const tweetProgress = Math.min((tweetCount / CAROUSEL_THRESHOLD) * 100, 100);
  const quoteProgress = Math.min((quoteCount / CAROUSEL_THRESHOLD) * 100, 100);
  const tweetReady = tweetCount >= CAROUSEL_THRESHOLD;
  const quoteReady = quoteCount >= CAROUSEL_THRESHOLD;

  // Filter logic
  const activeItems = activeTab === 'tweets' ? tweets
    : activeTab === 'quotes' ? quotes
    : activeTab === 'widget_tweets' ? widgetTweets
    : activeTab === 'rejected' ? rejectedItems
    : chesswebQueue.filter(i => i.status !== 'posted');
  const displayItems = (activeTab === 'chessweb' || activeTab === 'rejected')
    ? activeItems
    : viewMode === 'drafts'
      ? activeItems.filter(i => !i.reviewedAt)
      : activeItems.filter(i => !!i.reviewedAt);
  const unreviewedCount = (activeTab === 'chessweb' || activeTab === 'rejected') ? 0 : activeItems.filter(i => !i.reviewedAt).length;

  // For preview modal slide numbering
  const previewIndex = previewItem ? activeItems.findIndex(i => i.id === previewItem.id) : -1;

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-700 rounded w-1/3" />
          <div className="grid grid-cols-3 gap-4">
            <div className="h-32 bg-gray-800 rounded-xl" />
            <div className="h-32 bg-gray-800 rounded-xl" />
            <div className="h-32 bg-gray-800 rounded-xl" />
          </div>
          <div className="h-64 bg-gray-800 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      {/* Preview Modal */}
      {previewItem && (
        <PreviewModal
          item={previewItem}
          type={previewType}
          onClose={() => setPreviewItem(null)}
          slideNumber={previewIndex + 1}
          totalSlides={activeItems.length}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/admin')}
            className="text-xs text-gray-400 hover:text-gray-200 mb-1 flex items-center gap-1"
          >
            &larr; Admin
          </button>
          <h1 className="text-xl font-bold text-gray-100">Instagram Content Pipeline</h1>
          <p className="text-sm text-gray-400 mt-1">
            Publishing to: {activeTab === 'chessweb'
              ? <span className="text-green-400">@ChessWebsite</span>
              : activeTab === 'widget_tweets'
              ? <><span className="text-cyan-400">@TrendingChess</span> &middot; <span className="text-cyan-400">@chesstrending</span></>
              : <><span className="text-pink-400">@IndieChess</span> &middot; <span className="text-pink-400">@Trending.Chess</span></>
            }
          </p>
        </div>
        <button
          onClick={loadData}
          className="text-sm px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg transition-colors"
        >
          Refresh
        </button>
      </div>

      {/* Tabs + Controls */}
      <div className="flex items-center justify-between border-b border-gray-700 pb-0">
        <div className="flex gap-1.5 overflow-x-auto">
          <TabButton
            active={activeTab === 'tweets'}
            onClick={() => handleTabChange('tweets')}
            label={`Tweets (${filteredTweetCount})`}
          />
          <TabButton
            active={activeTab === 'quotes'}
            onClick={() => handleTabChange('quotes')}
            label={`Quotes (${filteredQuoteCount})`}
          />
          <TabButton
            active={activeTab === 'widget_tweets'}
            onClick={() => handleTabChange('widget_tweets')}
            label={`Widget (${widgetQueuePending > 0 ? widgetQueuePending + '+' : ''}${filteredWidgetTweetCount})`}
            color="cyan"
          />
          <TabButton
            active={activeTab === 'chessweb'}
            onClick={() => handleTabChange('chessweb')}
            label={`ChessWeb (${chesswebCount})`}
            color="green"
          />
          {rejectedCount > 0 && (
            <TabButton
              active={activeTab === 'rejected'}
              onClick={() => handleTabChange('rejected')}
              label={`Rejected (${rejectedCount})`}
              color="red"
            />
          )}
        </div>
        <div className="flex items-center gap-3 pb-1">
          {viewMode === 'drafts' && unreviewedCount > 0 && (
            <button
              onClick={handleBulkApprove}
              disabled={bulkApproving}
              className="text-xs px-3 py-1 bg-green-600/20 text-green-400 border border-green-600/30 rounded-lg hover:bg-green-600/30 transition-colors disabled:opacity-50"
            >
              {bulkApproving ? 'Approving...' : `Mark All Reviewed (${unreviewedCount})`}
            </button>
          )}
          <div className="flex items-center gap-1 bg-gray-800/50 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('drafts')}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                viewMode === 'drafts' ? 'bg-pink-600/30 text-pink-400' : 'text-gray-500 hover:text-gray-300'
              }`}
            >Drafts</button>
            <button
              onClick={() => setViewMode('approved')}
              className={`text-xs px-2.5 py-1 rounded-md transition-colors ${
                viewMode === 'approved' ? 'bg-green-600/30 text-green-400' : 'text-gray-500 hover:text-gray-300'
              }`}
            >Approved</button>
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="space-y-2">
        {/* Per-tab context bar */}
        {activeTab === 'tweets' && (
          <div className="flex items-center gap-2 text-xs px-1">
            <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full ${tweetReady ? 'bg-green-500' : 'bg-blue-500'} rounded-full`} style={{ width: `${tweetProgress}%` }} />
            </div>
            <span className="text-gray-400">{tweetCount}/{CAROUSEL_THRESHOLD}</span>
            {tweetReady
              ? <span className="text-green-400">Ready for Sunday carousel</span>
              : <span className="text-gray-500">Need {CAROUSEL_THRESHOLD - tweetCount} more</span>
            }
            {tweetCount > MAX_TWEET_ITEMS && <span className="text-yellow-400/80 ml-auto">Top 8 by score — rest expires</span>}
          </div>
        )}
        {activeTab === 'quotes' && (
          <div className="flex items-center gap-2 text-xs px-1">
            <div className="w-16 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full ${quoteReady ? 'bg-green-500' : 'bg-purple-500'} rounded-full`} style={{ width: `${quoteProgress}%` }} />
            </div>
            <span className="text-gray-400">{quoteCount}/{CAROUSEL_THRESHOLD}</span>
            {quoteReady
              ? <span className="text-green-400">Ready for Sunday carousel</span>
              : <span className="text-gray-500">Need {CAROUSEL_THRESHOLD - quoteCount} more</span>
            }
            {quoteCount > MAX_QUOTE_ITEMS && <span className="text-yellow-400/80 ml-auto">First 7 used — rest stays</span>}
          </div>
        )}
        {activeTab === 'widget_tweets' && (widgetQueuePending > 0 || widgetTweetCount > 0) && (
          <div className="text-xs text-cyan-400/80 px-1">
            {widgetQueuePending > 0 && <>{widgetQueuePending} awaiting variation pick</>}
            {widgetQueuePending > 0 && widgetTweetCount > 0 && ' · '}
            {widgetTweetCount > 0 && <>{widgetTweetCount} carousel item{widgetTweetCount !== 1 ? 's' : ''}</>}
          </div>
        )}
        {activeTab === 'chessweb' && chesswebPending > 0 && (
          <div className="text-xs text-green-400/80 px-1">
            {chesswebPending} awaiting variation pick — select, edit, publish
          </div>
        )}

        {activeTab === 'tweets' ? (
          displayItems.length === 0 ? (
            <ScheduleHint lines={
              viewMode === 'drafts'
                ? ['Fetch: Tue / Thu / Sat — scheduledTweetFetch (Cloud Fn, 8 AM UTC)', 'Post: Sun + Wed — Desktop scheduled task renders carousel → Post-Bridge']
                : ['Approved tweets appear here after review']
            } />
          ) : (
            displayItems.map((tweet, idx) => (
              <TweetItem
                key={tweet.id}
                tweet={tweet}
                index={idx}
                onRemove={handleRemove}
                onApprove={handleApprove}
                onPreview={() => handlePreview(tweet, 'tweet')}
                removing={removing === tweet.id}
                approving={approving === tweet.id}
                formatDate={formatDate}
              />
            ))
          )
        ) : activeTab === 'widget_tweets' ? (
          (widgetQueuePending === 0 && displayItems.length === 0) ? (
            <ScheduleHint lines={['/widget-spotlight and /widget-tweet — daily at 4 PM']} />
          ) : (
            <>
              {/* Widget Tweet Queue — select, edit, publish */}
              {widgetTweetQueue.filter(i => i.status !== 'posted').map((item) => (
                <WidgetTweetPostItem
                  key={`wq-${item.id}`}
                  item={item}
                  onSelect={handleSelectWidgetVariation}
                  onRemove={handleRemoveWidgetTweet}
                  onPublish={handlePublishWidgetTweet}
                  removing={removing === item.id}
                  selecting={selecting}
                  editing={widgetTweetEditing}
                  onEditChange={(text) => setWidgetTweetEditing(prev => prev ? { ...prev, text } : null)}
                  publishing={widgetTweetPublishing === item.id}
                  formatDate={formatDate}
                />
              ))}
              {/* Carousel queue items below — with Post-Bridge publish button */}
              {displayItems.length > 0 && (
                <>
                  {widgetQueuePending > 0 && (
                    <div className="text-xs text-gray-500 px-1 pt-2 border-t border-gray-700 mt-2">
                      Carousel Queue (from ig_carousel_content)
                    </div>
                  )}
                  {displayItems.map((tweet, idx) => (
                    <WidgetCarouselItem
                      key={tweet.id}
                      tweet={tweet}
                      index={idx}
                      onRemove={handleRemove}
                      onApprove={handleApprove}
                      onPreview={() => handlePreview(tweet, 'tweet')}
                      onPublish={() => handlePublishCarouselWidget(tweet)}
                      removing={removing === tweet.id}
                      approving={approving === tweet.id}
                      publishing={carouselPublishing === tweet.id}
                      formatDate={formatDate}
                    />
                  ))}
                </>
              )}
            </>
          )
        ) : activeTab === 'quotes' ? (
          displayItems.length === 0 ? (
            <ScheduleHint lines={
              viewMode === 'drafts'
                ? ['Fetch: Wed — /quote-research (Desktop, 12:06 PM)', 'Post: Sun + Wed — Desktop scheduled task renders carousel → Post-Bridge']
                : ['Approved quotes appear here after review']
            } />
          ) : (
            displayItems.map((quote, idx) => (
              <QuoteItem
                key={quote.id}
                quote={quote}
                index={idx}
                onRemove={handleRemove}
                onApprove={handleApprove}
                onPreview={() => handlePreview(quote, 'quote')}
                removing={removing === quote.id}
                approving={approving === quote.id}
                formatDate={formatDate}
              />
            ))
          )
        ) : activeTab === 'rejected' ? (
          displayItems.length === 0 ? (
            <div className="bg-gray-800/50 border border-gray-700 border-dashed rounded-lg p-8 text-center">
              <p className="text-sm text-gray-500">No auto-rejected items. Filters are working well!</p>
            </div>
          ) : (
            <>
              <div className="bg-yellow-900/20 border border-yellow-700/30 rounded-lg p-3 text-xs text-yellow-400">
                These tweets were auto-rejected by content filters (media-dependent or non-chess content). You can restore any that were incorrectly filtered.
              </div>
              {displayItems.map((tweet, idx) => (
                <RejectedTweetItem
                  key={tweet.id}
                  tweet={tweet}
                  onRestore={handleRestore}
                  onRemove={handleRemove}
                  restoring={restoring === tweet.id}
                  removing={removing === tweet.id}
                  formatDate={formatDate}
                />
              ))}
            </>
          )
        ) : activeTab === 'chessweb' ? (
          displayItems.length === 0 ? (
            <ScheduleHint lines={['Mon + Thu — /chessweb-social (Desktop, ~10 AM)']} />
          ) : (
            displayItems.map((item) => (
              <ChessWebPostItem
                key={item.id}
                item={item}
                onSelect={handleSelectVariation}
                onRemove={handleRemoveChessweb}
                onPublish={handlePublishChessweb}
                removing={removing === item.id}
                selecting={selecting}
                editing={chesswebEditing}
                onEditChange={(text) => setChesswebEditing(prev => prev ? { ...prev, text } : null)}
                publishing={chesswebPublishing === item.id}
                formatDate={formatDate}
              />
            ))
          )
        ) : null}
      </div>

      {/* Status Footer */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4 text-xs text-gray-400 space-y-1">
        <div className="flex justify-between">
          <span>Last fetch: {formatDateTime(pipelineStats.lastFetch)}</span>
          <span>Last carousel posted: {formatDateTime(pipelineStats.lastCarousel)}</span>
        </div>
        <p className="text-gray-500">
          Auto-fetch runs Tuesday, Thursday, Saturday at 8 AM UTC via Cloud Function
        </p>
      </div>
    </div>
  );
};

const ReviewBadge = ({ reviewed }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
    reviewed
      ? 'bg-green-500/20 text-green-400'
      : 'bg-orange-500/20 text-orange-400'
  }`}>
    {reviewed ? '✓ Reviewed' : 'Needs Review'}
  </span>
);


const TabButton = ({ active, onClick, label, color }) => {
  const borderColors = {
    green: 'border-green-500',
    cyan: 'border-cyan-500',
    red: 'border-red-500',
  };
  const borderColor = borderColors[color] || 'border-pink-500';
  return (
    <button
      onClick={onClick}
      className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-[1px] whitespace-nowrap ${
        active
          ? `text-gray-100 ${borderColor}`
          : 'text-gray-400 border-transparent hover:text-gray-200 hover:border-gray-600'
      }`}
    >
      {label}
    </button>
  );
};

const TweetItem = ({ tweet, index, onRemove, onApprove, onPreview, removing, approving, formatDate }) => {
  const m = tweet.metrics || {};

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-200">{tweet.author}</span>
            <span className="text-xs text-gray-500">{tweet.handle}</span>
            <ReviewBadge reviewed={!!tweet.reviewedAt} />
          </div>
          <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
            {tweet.text}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {m.likes > 0 && <span>{m.likes.toLocaleString()} &#9829;</span>}
            {m.retweets > 0 && <span>{m.retweets.toLocaleString()} &#128260;</span>}
            {m.replies > 0 && <span>{m.replies.toLocaleString()} &#128172;</span>}
            <span>{formatDate(tweet.createdAt)}</span>
            {tweet.source === 'scheduled' && (
              <span className="text-blue-400/60">auto</span>
            )}
            {tweet.score != null && (
              <span className="text-yellow-400/60">score: {tweet.score}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            onClick={onPreview}
            className="text-xs px-2 py-1 text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
          >
            Preview
          </button>
          {!tweet.reviewedAt && (
            <button
              onClick={() => onApprove(tweet.id)}
              disabled={approving}
              className="text-xs px-2 py-1 text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
            >
              {approving ? '...' : 'Approve'}
            </button>
          )}
          <button
            onClick={() => onRemove(tweet.id)}
            disabled={removing}
            className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
          >
            {removing ? '...' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
};

const WidgetCarouselItem = ({ tweet, index, onRemove, onApprove, onPreview, onPublish, removing, approving, publishing, formatDate }) => {
  return (
    <div className={`bg-gray-800 border rounded-lg p-4 transition-colors ${publishing ? 'border-cyan-500/50' : 'border-gray-700 hover:border-gray-600'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {tweet.widgetTitle && <span className="text-sm font-semibold text-yellow-400">{tweet.widgetTitle}</span>}
            <ReviewBadge reviewed={!!tweet.reviewedAt} />
            {tweet.postBridgePublished && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-cyan-500/20 text-cyan-400">
                Published
              </span>
            )}
          </div>
          <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap break-words">
            {tweet.text}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {tweet.slug && <span className="text-yellow-400/60">trendingchess.com/{tweet.slug}</span>}
            <span>{formatDate(tweet.createdAt)}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            onClick={onPreview}
            className="text-xs px-2 py-1 text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
          >
            Preview
          </button>
          {!tweet.postBridgePublished && (
            <button
              onClick={onPublish}
              disabled={publishing}
              className="text-xs px-2 py-1 text-cyan-400 hover:bg-cyan-500/10 rounded transition-colors disabled:opacity-50 font-medium"
            >
              {publishing ? 'Publishing...' : 'Publish X + IG'}
            </button>
          )}
          {!tweet.reviewedAt && (
            <button
              onClick={() => onApprove(tweet.id)}
              disabled={approving}
              className="text-xs px-2 py-1 text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
            >
              {approving ? '...' : 'Approve'}
            </button>
          )}
          <button
            onClick={() => onRemove(tweet.id)}
            disabled={removing}
            className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
          >
            {removing ? '...' : 'Remove'}
          </button>
        </div>
      </div>
    </div>
  );
};

const QuoteItem = ({ quote, index, onRemove, onApprove, onPreview, removing, approving, formatDate }) => (
  <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <ReviewBadge reviewed={!!quote.reviewedAt} />
        </div>
        <p className="text-sm text-gray-300 leading-relaxed italic">
          &ldquo;{quote.text}&rdquo;
        </p>
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          <span className="text-purple-400">{quote.player}</span>
          <span>{formatDate(quote.createdAt)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <button
          onClick={onPreview}
          className="text-xs px-2 py-1 text-blue-400 hover:bg-blue-500/10 rounded transition-colors"
        >
          Preview
        </button>
        {!quote.reviewedAt && (
          <button
            onClick={() => onApprove(quote.id)}
            disabled={approving}
            className="text-xs px-2 py-1 text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
          >
            {approving ? '...' : 'Approve'}
          </button>
        )}
        <button
          onClick={() => onRemove(quote.id)}
          disabled={removing}
          className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
        >
          {removing ? '...' : 'Remove'}
        </button>
      </div>
    </div>
  </div>
);

const ScheduleHint = ({ lines }) => (
  <div className="bg-gray-800/50 border border-gray-700 border-dashed rounded-lg p-6 text-center space-y-1">
    {lines.map((line, i) => (
      <p key={i} className="text-xs text-gray-500">{line}</p>
    ))}
  </div>
);


const REJECTION_REASONS = {
  media_dependent: { label: 'Media Required', color: 'bg-yellow-500/20 text-yellow-400' },
  not_chess_content: { label: 'Not Chess', color: 'bg-red-500/20 text-red-400' },
};

const RejectedTweetItem = ({ tweet, onRestore, onRemove, restoring, removing, formatDate }) => {
  const m = tweet.metrics || {};
  const reason = REJECTION_REASONS[tweet.rejectionReason] || { label: tweet.rejectionReason || 'Unknown', color: 'bg-gray-500/20 text-gray-400' };

  return (
    <div className="bg-gray-800 border border-red-900/30 rounded-lg p-4 opacity-80 hover:opacity-100 transition-opacity">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-semibold text-gray-200">{tweet.author}</span>
            <span className="text-xs text-gray-500">{tweet.handle}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${reason.color}`}>
              {reason.label}
            </span>
          </div>
          <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap break-words">
            {tweet.text}
          </p>
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
            {m.likes > 0 && <span>{m.likes.toLocaleString()} &#9829;</span>}
            {m.retweets > 0 && <span>{m.retweets.toLocaleString()} &#128260;</span>}
            <span>{formatDate(tweet.createdAt)}</span>
            {tweet.score != null && (
              <span className="text-yellow-400/60">score: {tweet.score}</span>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            onClick={() => onRestore(tweet.id)}
            disabled={restoring}
            className="text-xs px-2 py-1 text-green-400 hover:bg-green-500/10 rounded transition-colors disabled:opacity-50"
          >
            {restoring ? '...' : 'Restore'}
          </button>
          <button
            onClick={() => onRemove(tweet.id)}
            disabled={removing}
            className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
          >
            {removing ? '...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
};

const WidgetTweetPostItem = ({ item, onSelect, onRemove, onPublish, removing, selecting, editing, onEditChange, publishing, formatDate }) => {
  const variations = item.variations || [];
  const isPosted = item.status === 'posted';
  const isEditing = editing?.docId === item.id;
  const createdAt = item.createdAt?.toDate ? item.createdAt.toDate().toISOString() : item.createdAt;

  const charCount = isEditing ? (editing.text?.length || 0) : 0;
  const charColor = charCount > 280 ? 'text-red-400' : charCount > 260 ? 'text-yellow-400' : 'text-cyan-400';

  return (
    <div className={`bg-gray-800 border rounded-lg p-4 transition-colors ${isPosted ? 'border-cyan-500/50 opacity-75' : 'border-gray-700 hover:border-gray-600'}`}>
      {/* Widget context header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {item.widgetIcon && (
            <img src={item.widgetIcon} alt="" className="w-5 h-5 rounded" />
          )}
          <span className="text-sm font-semibold text-cyan-400">{item.widgetTitle || 'Widget'}</span>
          {item.widgetLink && (
            <a href={item.widgetLink} target="_blank" rel="noopener noreferrer" className="text-[10px] text-gray-500 hover:text-gray-300 truncate max-w-[200px]">
              {item.widgetLink.replace(/^https?:\/\//, '')}
            </a>
          )}
          <span className="text-xs text-gray-500">{formatDate(createdAt)}</span>
          {isPosted && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-cyan-500/20 text-cyan-400">
              Posted to Post-Bridge
            </span>
          )}
          {publishing && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-blue-500/20 text-blue-400 animate-pulse">
              Publishing...
            </span>
          )}
        </div>
        {!isPosted && (
          <button
            onClick={() => onRemove(item.id)}
            disabled={removing || publishing}
            className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
          >
            {removing ? '...' : 'Remove'}
          </button>
        )}
      </div>
      {item.context && (
        <p className="text-xs text-gray-500 mb-3 italic">{item.context}</p>
      )}

      {/* Posted state */}
      {isPosted ? (
        <div className="bg-gray-700/30 rounded-lg p-3">
          <p className="text-sm text-gray-300 leading-relaxed">{item.editedText || variations[item.selectedVariation]?.text}</p>
        </div>
      ) : (
        <>
          {/* Variation grid */}
          <div className="grid grid-cols-2 gap-2">
            {variations.map((v, idx) => {
              const isChosen = item.selectedVariation === idx;
              const isSelecting = selecting === `wt-${item.id}-${idx}`;
              return (
                <button
                  key={idx}
                  onClick={() => onSelect(item.id, idx)}
                  disabled={!!selecting || publishing}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    isChosen
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                  } disabled:opacity-70`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isChosen ? 'text-cyan-400' : 'text-gray-500'}`}>
                      #{idx + 1} {isChosen && '(selected)'}
                    </span>
                    <span className="text-[10px] text-gray-500">{v.charCount || v.text?.length} chars</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    {isSelecting ? 'Selecting...' : v.text}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Edit + Publish area */}
          {isEditing && (
            <div className="mt-4 space-y-3">
              <div className="relative">
                <textarea
                  value={editing.text}
                  onChange={(e) => onEditChange(e.target.value)}
                  disabled={publishing}
                  rows={4}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-sm text-gray-200 resize-none focus:outline-none focus:border-cyan-500 disabled:opacity-50"
                  placeholder="Edit tweet text..."
                />
                <span className={`absolute bottom-2 right-3 text-[11px] font-mono ${charColor}`}>
                  {charCount}/280
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500">
                  Tweet (@TrendingChess) + IG text (@chesstrending) → Post-Bridge drafts
                </span>
                <button
                  onClick={() => onPublish(item.id)}
                  disabled={publishing || !editing.text?.trim() || charCount > 280}
                  className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {publishing ? 'Publishing...' : 'Publish to Post-Bridge'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const CONTENT_TYPE_COLORS = {
  feature: { bg: 'bg-blue-500/20', text: 'text-blue-400' },
  blog: { bg: 'bg-purple-500/20', text: 'text-purple-400' },
  demo: { bg: 'bg-red-500/20', text: 'text-red-400' },
  usecase: { bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  general: { bg: 'bg-gray-500/20', text: 'text-gray-400' },
};

const ChessWebPostItem = ({ item, onSelect, onRemove, onPublish, removing, selecting, editing, onEditChange, publishing, formatDate }) => {
  const variations = item.variations || [];
  const isPosted = item.status === 'posted';
  const isEditing = editing?.docId === item.id;
  const typeStyle = CONTENT_TYPE_COLORS[item.contentType] || CONTENT_TYPE_COLORS.general;
  const createdAt = item.createdAt?.toDate ? item.createdAt.toDate().toISOString() : item.createdAt;

  const charCount = isEditing ? (editing.text?.length || 0) : 0;
  const charColor = charCount > 280 ? 'text-red-400' : charCount > 260 ? 'text-yellow-400' : 'text-green-400';

  return (
    <div className={`bg-gray-800 border rounded-lg p-4 transition-colors ${isPosted ? 'border-green-500/50 opacity-75' : 'border-gray-700 hover:border-gray-600'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${typeStyle.bg} ${typeStyle.text}`}>
            {item.contentType || 'general'}
          </span>
          <span className="text-xs text-gray-500">{formatDate(createdAt)}</span>
          {isPosted && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-green-500/20 text-green-400">
              Posted to Post-Bridge
            </span>
          )}
          {publishing && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-blue-500/20 text-blue-400 animate-pulse">
              Publishing...
            </span>
          )}
        </div>
        {!isPosted && (
          <button
            onClick={() => onRemove(item.id)}
            disabled={removing || publishing}
            className="text-xs px-2 py-1 text-red-400 hover:bg-red-500/10 rounded transition-colors disabled:opacity-50"
          >
            {removing ? '...' : 'Remove'}
          </button>
        )}
      </div>
      {item.context && (
        <p className="text-xs text-gray-500 mb-3 italic">{item.context}</p>
      )}

      {/* Posted state — show final text */}
      {isPosted ? (
        <div className="bg-gray-700/30 rounded-lg p-3">
          <p className="text-sm text-gray-300 leading-relaxed">{item.editedText || variations[item.selectedVariation]?.text}</p>
        </div>
      ) : (
        <>
          {/* Variation grid */}
          <div className="grid grid-cols-2 gap-2">
            {variations.map((v, idx) => {
              const isChosen = item.selectedVariation === idx;
              const isSelecting = selecting === `${item.id}-${idx}`;
              return (
                <button
                  key={idx}
                  onClick={() => onSelect(item.id, idx)}
                  disabled={!!selecting || publishing}
                  className={`text-left p-3 rounded-lg border transition-all ${
                    isChosen
                      ? 'border-green-500 bg-green-500/10'
                      : 'border-gray-600 hover:border-gray-500 bg-gray-700/50'
                  } disabled:opacity-70`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[10px] font-bold ${isChosen ? 'text-green-400' : 'text-gray-500'}`}>
                      #{idx + 1} {isChosen && '(selected)'}
                    </span>
                    <span className="text-[10px] text-gray-500">{v.charCount || v.text?.length} chars</span>
                  </div>
                  <p className="text-xs text-gray-300 leading-relaxed">
                    {isSelecting ? 'Selecting...' : v.text}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Edit + Publish area — shown when a variation is selected */}
          {isEditing && (
            <div className="mt-4 space-y-3">
              <div className="relative">
                <textarea
                  value={editing.text}
                  onChange={(e) => onEditChange(e.target.value)}
                  disabled={publishing}
                  rows={4}
                  className="w-full bg-gray-900 border border-gray-600 rounded-lg p-3 text-sm text-gray-200 resize-none focus:outline-none focus:border-green-500 disabled:opacity-50"
                  placeholder="Edit tweet text..."
                />
                <span className={`absolute bottom-2 right-3 text-[11px] font-mono ${charColor}`}>
                  {charCount}/280
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500">
                  Tweet + IG carousel will be sent to Post-Bridge as drafts
                </span>
                <button
                  onClick={() => onPublish(item.id)}
                  disabled={publishing || !editing.text?.trim() || charCount > 280}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {publishing ? 'Publishing...' : 'Publish to Post-Bridge'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InstagramDashboard;
